import _ from 'lodash';
import Link from '../../ui/Link';
import {generateError} from '../validations/errors';
import reporter from '../reporter';
import {validate, validateByType, TYPES} from '../validations/validation';
import imageUtils from '../utils/imageUtils';
import vectorImageUtils from '../utils/vectorImageUtils';
import linkGenerator from '../utils/linkGenerator';
import LinkType from '../utils/LinkType';

const dataItemToUserFormatPropsMap = {
    label: 'label',
    items: 'items',
    link: 'link',
    isVisible: 'visibleOnDesktop',
    isVisibleMobile: 'visibleOnMobile',
    icon: 'iconRef',
    displayCount: 'displayCount'
};

const BasicMenuItemType = 'BasicMenuItem';
const compiled = _.template('<%= menuItemIndex %>,<%= subItemIndex %>');

function convertIconToModelFormat(icon, functionName, menuItemIndex) {
    const isValidImage = imageUtils.isValidImage(icon);
    if (isValidImage) {
        const model = imageUtils.extractModelDataFromSrc(icon);
        return _.assign(model, {type: 'Image'});
    }

    const isValidSvg = vectorImageUtils.isValidInternalVectorImage(icon); // this is a hack until WEED-13787 would be solved
    if (isValidSvg) {
        const model = vectorImageUtils.extractModelDataFromSrc(icon);
        return _.assign(model, {type: 'VectorImage'});
    }

    const error = !_.isNil(menuItemIndex) ? generateError.error_bad_menu_item_format_with_index : generateError.error_bad_menu_item_format;
    reporter.reportSdkError(error({
        propertyName: 'icon',
        functionName,
        index: menuItemIndex,
        wrongValue: icon
    }));

    return null;
}

function convertIconRefToUserFormat(iconRef) {
    if (iconRef.svgId) {
        return vectorImageUtils.buildFullUrlFromSvgId(iconRef.svgId, iconRef.title);
    }
    return imageUtils.buildFullUrlFromUri(iconRef.uri, iconRef.width, iconRef.height, iconRef.title);
}

function convertMenuItemToModelFormat(RMI, menuItem, menuItemIndex, functionName, labelOptional) {
    const menuItemModelFormat = {};
    const originalLink = menuItem.link;
    const link = linkGenerator.convertLinkToModelFormatAndReportIfError(menuItem.link, null, functionName, menuItemIndex);
    if (link) {
        menuItemModelFormat.link = link;
    }
    if (!_.isNil(menuItem.label) && validateByType({
        value: menuItem.label,
        expectedType: TYPES.STRING
    })) {
        menuItemModelFormat.label = menuItem.label;
    }
    if (!_.isNil(menuItem.visibleOnDesktop) && validateByType({
        value: menuItem.visibleOnDesktop,
        expectedType: TYPES.BOOLEAN
    })) {
        menuItemModelFormat.isVisible = menuItem.visibleOnDesktop;
    }
    if (!_.isNil(menuItem.visibleOnMobile) && validateByType({
        value: menuItem.visibleOnMobile,
        expectedType: TYPES.BOOLEAN
    })) {
        menuItemModelFormat.isVisibleMobile = menuItem.visibleOnMobile;
    }
    if (!_.isNil(menuItem.items) && validateByType({
        value: menuItem.items,
        expectedType: TYPES.ARRAY
    })) {
        if (!_.isEmpty(menuItem.items)) {
            menuItemModelFormat.items = _.map(menuItem.items, (subItem, subItemIndex) => {
                return convertMenuItemToModelFormat(RMI, subItem, compiled({
                    menuItemIndex,
                    subItemIndex
                }), functionName);
            });
        } else {
            menuItemModelFormat.items = menuItem.items;
        }

    }
    if (!_.isNil(menuItem.icon)) {
        const iconData = convertIconToModelFormat(menuItem.icon, functionName, menuItemIndex);
        if (iconData) {
            menuItemModelFormat.iconRef = iconData;
        }
    }
    if (_.has(menuItem, 'displayCount') && (_.isNil(menuItem.displayCount) || validateByType({
        value: menuItem.displayCount,
        expectedType: TYPES.NUMBER
    }))) {
        menuItemModelFormat.displayCount = menuItem.displayCount;
    }
    if (!_.isNil(menuItem.label) && validateByType({
        value: menuItem.label,
        expectedType: TYPES.STRING
    })) {
        menuItemModelFormat.label = menuItem.label;
    }
    if (!labelOptional && menuItemModelFormat.link && menuItemModelFormat.link.type === LinkType.PAGE && !menuItemModelFormat.label) {
        const siteStructure = RMI.getSiteStructure() || {};
        const item = _.find(siteStructure.pages, {url: originalLink});
        menuItemModelFormat.label = item.name;
    }
    if (!labelOptional && !menuItemModelFormat.label) {
        reporter.reportSdkWarning(generateError.warning_not_null_with_index({
            propertyName: 'label',
            functionName,
            index: menuItemIndex
        }));
    }
    _.defaults(menuItemModelFormat, {
        type: BasicMenuItemType,
        isVisible: true,
        isVisibleMobile: true,
        items: []
    });
    return menuItemModelFormat;
}

function convertMenuItemToUserFormat(menuItem) {
    return _.reduce(menuItem, (res, val, key) => {
        if (_.isNil(val)){
            return res;
        }
        const prop = dataItemToUserFormatPropsMap[key];
        if (prop){
            res[prop] = val;
        }
        if (key === 'link') {
            const link = new Link(val);
            res.link = link ? link.getValue() : null;
        }
        if (key === 'iconRef' && !_.isEmpty(val)) {
            res.icon = convertIconRefToUserFormat(val);
        }
        if (key === 'items' && !_.isEmpty(val)){
            res.items = _.map(val, convertMenuItemToUserFormat);
        }
        return res;
    }, {});
}

function removeUnsupportedProps(navItem, additionalUnsupportedProps){
    return _.omit(navItem, ['items', 'type'].concat(additionalUnsupportedProps || []));
}

function convertNavItemToUserFormat(navItem, unsupportedProps) {
    const userFormatNavItem = convertMenuItemToUserFormat(navItem);
    return removeUnsupportedProps(userFormatNavItem, unsupportedProps);
}

function removeNonValidMenuLevelsAndReport(menuItem, menuItemIndex, functionName) {
    if (_.isEmpty(menuItem.items)) {
        return true;
    }
    let hasMoreThanTwoLevels = false;
    let index;

    _.forEach(menuItem.items, (subItem, subItemIndex) => {
        if (_.isEmpty(subItem.items)) {
            return true;
        }
        hasMoreThanTwoLevels = true;
        index = compiled({menuItemIndex, subItemIndex});
        delete subItem.items;
    });

    if (hasMoreThanTwoLevels) {
        reporter.reportSdkError(generateError.error_menu_can_have_two_levels({
            propertyName: 'label',
            functionName,
            index
        }));
    }
}

function checkMenuItemValidation({menuItem, menuItemIndex, functionName, elementID}) {
    const isValid = validate({
        compType: BasicMenuItemType,
        functionName,
        dataToValidate: menuItem,
        shouldCheckMandatoryFields: true,
        itemIndex: menuItemIndex,
        elementID
    });
    removeNonValidMenuLevelsAndReport(menuItem, menuItemIndex, functionName);

    return isValid;
}

function checkMenuItemsValidation({menuItems, functionName, elementID}) {
    _.forEach(menuItems, (menuItem, menuItemIndex) => {
        delete menuItem.icon; //TODO : remove once the add the icon support
        checkMenuItemValidation({menuItem, menuItemIndex, functionName, elementID});
        _.forEach(menuItem.items, (subItem, subItemIndex) => {
            delete subItem.icon; //TODO : remove once the add the icon support
            checkMenuItemValidation({
                menuItem: subItem,
                menuItemIndex: compiled({ menuItemIndex, subItemIndex }),
                functionName,
                elementID
            });
        });
    });
}

function checkUserProfileItemsValidation({navItems, functionName, additionalUnsupportedProps, elementID}){
    return _.reduce(navItems, (res, navItem, navItemIndex) => {
        const navItemWithSupportedProps = removeUnsupportedProps(navItem, additionalUnsupportedProps);
        checkMenuItemValidation({
            menuItem: navItemWithSupportedProps,
            menuItemIndex: navItemIndex,
            functionName,
            elementID
        });
         res.push(navItemWithSupportedProps);
         return res;
    }, []);
}

function checkUserProfileNavBarItemsValidation ({navItems, functionName, elementID}){
    return checkUserProfileItemsValidation({
        navItems,
        functionName,
        additionalUnsupportedProps: ['label'],
        elementID
    });
}

function checkUserProfileTinyMenuNavBarItemsValidation ({navItems, functionName, elementID}){
    return checkUserProfileItemsValidation({
        navItems,
        functionName,
        additionalUnsupportedProps: ['visibleOnDesktop'],
        elementID
    });
}


function checkUserProfileTinyMenuItemsValidation({navItems, functionName, elementID}) {
    return checkUserProfileItemsValidation({
        navItems,
        functionName,
        additionalUnsupportedProps: ['icon', 'visibleOnDesktop'],
        elementID
    });
}

function checkUserProfileMenuItemsValidation({navItems, functionName, elementID}) {
    return checkUserProfileItemsValidation({
        navItems,
        functionName,
        additionalUnsupportedProps: ['icon'],
        elementID
    });
}


export default {
    convertMenuItemToModelFormat,
    convertMenuItemToUserFormat,
    convertNavItemToUserFormat,
    checkMenuItemsValidation,
    checkUserProfileMenuItemsValidation,
    checkUserProfileTinyMenuItemsValidation,
    checkUserProfileNavBarItemsValidation,
    checkUserProfileTinyMenuNavBarItemsValidation
};
